import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import HumanoidIntelligenceShell, { UnknownValue } from "../../components/humanoid/HumanoidIntelligenceShell";
import { getPrimaryPrice, getPurchasePriceLabel, getSpecificationLabel, humanoidCatalogStats, humanoidRobots } from "../../data/humanoidCatalog";
import { handEvidence, sensorEvidence } from "../../data/humanoidIntelligence";

export function matchingSensorEvidence(robot) {
  return sensorEvidence.filter((entry) => entry.present && entry.robot.toLowerCase() === robot.name.toLowerCase());
}

export function matchingHandEvidence(robot) {
  const name = robot.name.toLowerCase();
  return handEvidence.filter((entry) => {
    const evidenceName = entry.robot.toLowerCase();
    return evidenceName === name || evidenceName.startsWith(`${name} `);
  });
}

function sensorList(robot) {
  const rows = matchingSensorEvidence(robot);
  if (!rows.length) return "No matching model in the retained sensor data";
  return <ul className="space-y-2">{rows.map((row) => <li key={`${row.robot}-${row.sensor}`}><a href={row.sourceUrl} target="_blank" rel="noreferrer" className="underline">{row.sensor}</a>{row.notes && <span className="block text-xs text-slate-400">{row.notes}</span>}</li>)}</ul>;
}

function handSummary(robot) {
  const rows = matchingHandEvidence(robot);
  if (!rows.length) return "No matching model in the retained hand data";
  return <ul className="space-y-3">{rows.map((row) => <li key={row.robot}><a href={row.sourceUrl} target="_blank" rel="noreferrer" className="font-bold underline">{row.robot}</a><p>{[Number.isFinite(row.fingers) ? `${row.fingers} fingers` : null, Number.isFinite(row.joints) ? `${row.joints} joints` : null, row.tactileSensors || null].filter(Boolean).join("; ")}</p>{row.jointScope && <p className="text-xs text-slate-400">{row.jointScope}</p>}{row.notes && <p className="text-xs text-slate-400">{row.notes}</p>}</li>)}</ul>;
}

const defaultIds = humanoidRobots.slice(0, 4).map((robot) => robot.id);

export default function HumanoidComparePage() {
  const [selectedIds, setSelectedIds] = useState(defaultIds);
  const selected = useMemo(() => selectedIds.map((id) => humanoidRobots.find((robot) => robot.id === id)).filter(Boolean), [selectedIds]);

  function updateSlot(index, value) {
    setSelectedIds((current) => {
      const next = [...current];
      next[index] = value;
      return [...new Set(next.filter(Boolean))].slice(0, 4);
    });
  }

  function addSlot() {
    const next = humanoidRobots.find((robot) => !selectedIds.includes(robot.id));
    if (next && selectedIds.length < 4) setSelectedIds((current) => [...current, next.id]);
  }

  const rows = [
    ["Manufacturer", (robot) => robot.manufacturerName],
    ["Country", (robot) => robot.robotOriginCountry],
    ["Status", (robot) => robot.status],
    ["Availability", (robot) => robot.availability],
    ["Purchase price", (robot) => getPurchasePriceLabel(robot)],
    ["First recorded price plan", (robot) => getPrimaryPrice(robot)],
    ["Height", (robot) => getSpecificationLabel(robot, "heightCm")],
    ["Weight", (robot) => getSpecificationLabel(robot, "weightKg")],
    ["Degrees of freedom", (robot) => getSpecificationLabel(robot, "degreesOfFreedom")],
    ["Sensors retained", (robot) => sensorList(robot)],
    ["Named hand configurations", (robot) => handSummary(robot)],
    ["Target markets", (robot) => (robot.targetMarkets || []).join(", ") || "Not publicly disclosed"],
    ["Use cases", (robot) => robot.useCases || "Not publicly disclosed"],
    ["Catalog source date", (robot) => robot.lastVerifiedAt],
    ["Specification notes", (robot) => robot.specificationNotes || "See the linked product documentation"],
    ["Official documentation", (robot) => <a href={robot.officialProductUrl} target="_blank" rel="noreferrer" className="text-cyan-200 underline">{robot.name} product source</a>],
  ];

  return (
    <HumanoidIntelligenceShell
      title="Humanoid Robot Comparison"
      description="Select up to four humanoid robots and compare only the specifications and evidence currently retained in the TechniaHQRobot datasets. Missing values remain undisclosed rather than estimated."
      path="/humanoid-robots/compare"
      eyebrow="SIDE-BY-SIDE EVIDENCE"
      recordCount={humanoidRobots.length}
      modifiedTime={humanoidCatalogStats.lastVerifiedAt}
      reviewLabel={`Most recent catalog source review ${humanoidCatalogStats.lastVerifiedAt}; dates vary by record`}
    >
      <section className="border border-white/10 bg-[#07101f]/90 p-4 sm:p-5">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {selectedIds.map((id, index) => (
            <label key={`${id}-${index}`} className="block text-xs font-black uppercase tracking-[0.13em] text-slate-400">
              Robot {index + 1}
              <select value={id} onChange={(event) => updateSlot(index, event.target.value)} className="mt-2 w-full border border-white/15 bg-[#030712] px-3 py-3 text-sm font-bold normal-case tracking-normal text-white">
                {humanoidRobots.map((robot) => <option key={robot.id} value={robot.id}>{robot.name}</option>)}
              </select>
            </label>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          {selectedIds.length < 4 && <button type="button" onClick={addSlot} className="border border-cyan-300/30 bg-cyan-300/10 px-4 py-2 text-sm font-black text-cyan-100">Add robot</button>}
          {selectedIds.length > 2 && <button type="button" onClick={() => setSelectedIds((current) => current.slice(0, -1))} className="border border-white/15 bg-white/[0.04] px-4 py-2 text-sm font-black text-slate-300">Remove last</button>}
        </div>
      </section>

      <section className="mt-6 overflow-x-auto border border-white/10">
        <table className="w-full min-w-[900px] border-collapse text-left text-sm">
          <thead className="bg-white/[0.06]">
            <tr>
              <th className="w-44 px-4 py-4 text-xs font-black uppercase tracking-[0.13em] text-slate-500">Metric</th>
              {selected.map((robot) => (
                <th key={robot.id} className="min-w-[220px] px-4 py-4 align-top">
                  <Link to={`/humanoid-robots/${robot.slug}`} className="text-lg font-black text-white hover:text-cyan-300">{robot.name}</Link>
                  <p className="mt-1 text-xs font-bold text-slate-500">{robot.manufacturerName}</p>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10 bg-[#07101f]/85">
            {rows.map(([label, resolver]) => (
              <tr key={label} className="align-top">
                <th scope="row" className="px-4 py-4 text-xs font-black uppercase tracking-[0.1em] text-cyan-300">{label}</th>
                {selected.map((robot) => <td key={`${label}-${robot.id}`} className="px-4 py-4 leading-6 text-slate-300"><UnknownValue value={resolver(robot)} /></td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="mt-6 border border-white/10 bg-white/[0.035] p-5 text-sm leading-7 text-slate-400">
        Purchase prices exclude recurring plans and deposits. Each hand entry names its configuration; an optional hand is not assumed to be included in the base robot price. Sensor rows require the same model name. The figures retain their source conditions and do not form a performance ranking.
      </section>
    </HumanoidIntelligenceShell>
  );
}
